import {
  Country,
  PyramidType,
  PyramidValueDto,
  ReferencePyramidDetail,
  Option,
  PyramidForecastValues,
  PyramidForecastValueDto,
  EmployeeLevelEnum,
} from '../../types'
import i18next from 'i18next'
import { LimitType } from './shared/PyramidLimitType.enum'

export type PyramidsData = {
  name: string
  past?: number
  current?: number
  forecast?: number
  limitType?: string
  recommended?: number
  recommendedRange?: [number, number]
}

export type DashbaordPyramidsData = {
  name: string
  current: number
  currentCount: number
  recommendedRange?: [number, number]
}

export type PyramidsColumnGraphSeries = {
  percentage: number
  count: number
}

export type PyramidsColumnGraphData = {
  name: string
  past?: PyramidsColumnGraphSeries
  current?: PyramidsColumnGraphSeries
  forecast?: PyramidsColumnGraphSeries
  recommendedRange?: [number, number]
}

export function formatPyramidValues(
  pyramidValues: PyramidValueDto[],
  country: Country,
  type: PyramidType,
  employeeLevels: EmployeeLevelEnum[],
): PyramidValueDto[] {
  if (!pyramidValues || !pyramidValues.length) {
    return []
  }

  if (type === PyramidType.Age) {
    return fixDisplayOrderForAgePyramids(pyramidValues)
  }

  if (isR3Pyramid(country) || isR2Pyramid(country)) {
    const formatedValues: PyramidValueDto[] = formatLevelPyramidValuesPyramids(pyramidValues)
    return fixDisplayOrderForLevelPyramids(formatedValues, country, employeeLevels)
  } else {
    const formatedValues: PyramidValueDto[] = formatLevelPyramidValuesForNonStandardPyramids(pyramidValues)
    return fixDisplayOrderForLevelPyramids(formatedValues, country, employeeLevels)
  }
}

const formatLevelPyramidValuesPyramids = (pyramidValues: PyramidValueDto[]): PyramidValueDto[] => {
  const c1PyramidValues: PyramidValueDto | undefined = pyramidValues.find((value) => value.name === 'C1')
  const c2PyramidValues: PyramidValueDto | undefined = pyramidValues.find((value) => value.name === 'C2')

  const prPyramidValues: PyramidValueDto | undefined = pyramidValues.find((value) => value.name === 'PR')
  const mPyramidValues: PyramidValueDto | undefined = pyramidValues.find((value) => value.name === 'M')

  const sprPyramidValues: PyramidValueDto | undefined = pyramidValues.find((value) => value.name === 'SPR')
  const smPyramidValues: PyramidValueDto | undefined = pyramidValues.find((value) => value.name === 'SM')

  if (c1PyramidValues && c2PyramidValues && prPyramidValues && mPyramidValues && sprPyramidValues && smPyramidValues) {
    const pyramidValuesForR3: PyramidValueDto[] = []

    pyramidValues.forEach((value) => {
      if (!['C1', 'C2', 'PR', 'M', 'SPR', 'SM'].includes(value.name)) {
        pyramidValuesForR3.push({ ...value })
      }
    })

    pyramidValuesForR3.push(
      {
        name: 'C1 - C2',
        percentage: c1PyramidValues.percentage + c2PyramidValues.percentage,
        displayOrder: 0,
        count: c1PyramidValues.count + c2PyramidValues.count,
      },
      {
        name: 'PR - M',
        percentage: prPyramidValues.percentage + mPyramidValues.percentage,
        displayOrder: 0,
        count: prPyramidValues.count + mPyramidValues.count,
      },
      {
        name: 'SPR - SM',
        percentage: sprPyramidValues.percentage + smPyramidValues.percentage,
        displayOrder: 0, // this has to be added after for all values
        count: sprPyramidValues.count + smPyramidValues.count,
      },
    )

    return pyramidValuesForR3
  }

  return pyramidValues
}

const formatLevelPyramidValuesForNonStandardPyramids = (pyramidValues: PyramidValueDto[]): PyramidValueDto[] => {
  const c1PyramidValues: PyramidValueDto | undefined = pyramidValues.find((value) => value.name === 'C1')
  const c2PyramidValues: PyramidValueDto | undefined = pyramidValues.find((value) => value.name === 'C2')
  if (c1PyramidValues && c2PyramidValues) {
    const pyramidValuesForNonDeu: PyramidValueDto[] = []
    pyramidValues.forEach((value) => {
      if (value.name !== 'C1' && value.name !== 'C2') {
        pyramidValuesForNonDeu.push({ ...value })
      }
    })
    pyramidValuesForNonDeu.push({
      name: 'C1 - C2',
      percentage: c1PyramidValues.percentage + c2PyramidValues.percentage,
      displayOrder: 0, // this has to be added after for all values
      count: c1PyramidValues.count + c2PyramidValues.count,
    })
    return pyramidValuesForNonDeu
  }

  return pyramidValues
}

function fixDisplayOrderForAgePyramids(pyramidValues: PyramidValueDto[]) {
  const pyramidsWithFixedDisplayOrder: PyramidValueDto[] = []
  for (const value of pyramidValues) {
    pyramidsWithFixedDisplayOrder.push({ ...value, displayOrder: getDisplayOrderByAgeRange(value.name) })
  }
  return pyramidsWithFixedDisplayOrder
}

// common function for all pyramid types
const fixDisplayOrderForLevelPyramids = (
  pyramidValues: PyramidValueDto[],
  country: Country,
  employeeLevels: EmployeeLevelEnum[],
): PyramidValueDto[] => {
  const pyramidsWithFixedDisplayOrder: PyramidValueDto[] = []
  for (const value of pyramidValues) {
    pyramidsWithFixedDisplayOrder.push({ ...value, displayOrder: getDisplayOrderByRoleNameAndCountry(value.name, country, employeeLevels) })
  }
  return pyramidsWithFixedDisplayOrder
}

// reference pyramid helpers
export const formatPyramidReference = (
  pyramidValues: ReferencePyramidDetail[],
  country: Country,
  type: PyramidType,
  employeeLevels: EmployeeLevelEnum[],
): ReferencePyramidDetail[] => {
  if (!pyramidValues || !pyramidValues.length) {
    return []
  }

  if (type === PyramidType.Age) {
    return fixDisplayOrderForAgeReferencePyramid(pyramidValues)
  }

  if (isR3Pyramid(country) || isR2Pyramid(country)) {
    const formatedValues: ReferencePyramidDetail[] = formatRecomendationPyramids(pyramidValues)
    return fixDisplayOrderForLevelReferencePyramid(formatedValues, country, employeeLevels)
  } else {
    const formatedValues: ReferencePyramidDetail[] = formatRecomendationForNonStandardPyramids(pyramidValues)
    return fixDisplayOrderForLevelReferencePyramid(formatedValues, country, employeeLevels)
  }
}

const formatRecomendationForNonStandardPyramids = (recommendation: ReferencePyramidDetail[]): ReferencePyramidDetail[] => {
  const c1PyramidValues: ReferencePyramidDetail | undefined = recommendation.find((value) => value.range === 'C1')
  const c2PyramidValues: ReferencePyramidDetail | undefined = recommendation.find((value) => value.range === 'C2')
  if (c1PyramidValues && c2PyramidValues) {
    const pyramidValuesForNonDeu: ReferencePyramidDetail[] = []

    recommendation.forEach((value) => {
      if (value.range !== 'C1' && value.range !== 'C2') {
        pyramidValuesForNonDeu.push({ ...value })
      }
    })

    pyramidValuesForNonDeu.push({
      id: 0,
      displayOrder: 0, // this has to be added in after formating for every range
      range: 'C1 - C2',
      limitType: c1PyramidValues.limitType, // the limit types should be the same
      percentage: c1PyramidValues.percentage + c2PyramidValues.percentage,
    })

    return pyramidValuesForNonDeu
  }
  return recommendation
}

const formatRecomendationPyramids = (recommendation: ReferencePyramidDetail[]): ReferencePyramidDetail[] => {
  const c1PyramidValues: ReferencePyramidDetail | undefined = recommendation.find((value) => value.range === 'C1')
  const c2PyramidValues: ReferencePyramidDetail | undefined = recommendation.find((value) => value.range === 'C2')

  const prPyramidValues: ReferencePyramidDetail | undefined = recommendation.find((value) => value.range === 'PR')
  const mPyramidValues: ReferencePyramidDetail | undefined = recommendation.find((value) => value.range === 'M')

  const sprPyramidValues: ReferencePyramidDetail | undefined = recommendation.find((value) => value.range === 'SPR')
  const smPyramidValues: ReferencePyramidDetail | undefined = recommendation.find((value) => value.range === 'SM')
  if (c1PyramidValues && c2PyramidValues && prPyramidValues && mPyramidValues && sprPyramidValues && smPyramidValues) {
    const pyramidValuesForR3: ReferencePyramidDetail[] = []

    recommendation.forEach((value) => {
      if (!['C1', 'C2', 'PR', 'M', 'SPR', 'SM'].includes(value.range)) {
        pyramidValuesForR3.push({ ...value })
      }
    })

    pyramidValuesForR3.push(
      {
        id: 0,
        displayOrder: 0,
        range: 'C1 - C2',
        limitType: c1PyramidValues.limitType,
        percentage: c1PyramidValues.percentage + c2PyramidValues.percentage,
      },
      {
        id: 0,
        displayOrder: 0,
        range: 'PR - M',
        limitType: prPyramidValues.limitType,
        percentage: prPyramidValues.percentage + mPyramidValues.percentage,
      },
      {
        id: 0,
        displayOrder: 0,
        range: 'SPR - SM',
        limitType: sprPyramidValues.limitType, // the limit types should be the same
        percentage: sprPyramidValues.percentage + smPyramidValues.percentage,
      },
    )

    return pyramidValuesForR3
  }
  return recommendation
}

function fixDisplayOrderForAgeReferencePyramid(pyramidValues: ReferencePyramidDetail[]) {
  const pyramidsWithFixedDisplayOrder: ReferencePyramidDetail[] = []
  for (const value of pyramidValues) {
    const newValue = { ...value, displayOrder: getDisplayOrderByAgeRange(value.range) }

    pyramidsWithFixedDisplayOrder.push({ ...newValue })
  }
  return pyramidsWithFixedDisplayOrder
}

function fixDisplayOrderForLevelReferencePyramid(
  pyramidValues: ReferencePyramidDetail[],
  country: Country,
  employeeLevels: EmployeeLevelEnum[],
): ReferencePyramidDetail[] {
  const pyramidsWithFixedDisplayOrder: ReferencePyramidDetail[] = []
  for (const value of pyramidValues) {
    pyramidsWithFixedDisplayOrder.push({
      ...value,
      displayOrder: getDisplayOrderByRoleNameAndCountry(value.range, country, employeeLevels),
    })
  }
  return pyramidsWithFixedDisplayOrder
}

function getDisplayOrderByAgeRange(ageRange: string) {
  switch (ageRange) {
    case '< 30':
      return 1
    case '30 - 35':
      return 2
    case '36 - 40':
      return 3
    case '41 - 45':
      return 4
    case '> 45':
      return 5
    default:
      return 6
  }
}

function getDisplayOrderByRoleNameAndCountry(roleName: string, country: Country, employeeLevels: EmployeeLevelEnum[]) {
  if (!employeeLevels || !employeeLevels.length) {
    return 0
  }

  const levels = [...employeeLevels].reverse()

  if (isR3Pyramid(country) || isR2Pyramid(country)) {
    let _levels = levels.slice()
    _levels = _levels
      .filter((l) => ![EmployeeLevelEnum.C1, EmployeeLevelEnum.Pr, EmployeeLevelEnum.Spr].includes(l))
      .map((l) => {
        return l === EmployeeLevelEnum.C2
          ? ('C1 - C2' as EmployeeLevelEnum)
          : l === EmployeeLevelEnum.M
          ? ('PR - M' as EmployeeLevelEnum)
          : l === EmployeeLevelEnum.Sm
          ? ('SPR - SM' as EmployeeLevelEnum)
          : l
      })
    return _levels.indexOf(roleName as EmployeeLevelEnum) + 1
  } else {
    const _levels = levels.slice()
    _levels.splice(_levels.indexOf(EmployeeLevelEnum.C2), 0, 'C1 - C2' as EmployeeLevelEnum)
    return _levels.indexOf(roleName as EmployeeLevelEnum) + 1
  }
}

export function isR2Pyramid(country: Country): boolean {
  return [Country.Deu, Country.Hrv, Country.Pol].includes(country)
}

export function isR3Pyramid(country: Country): boolean {
  return [Country.Fra, Country.Mar, Country.Blr, Country.Bel, Country.Lux, Country.Nld, Country.Gbr].includes(country)
}

export const formatPyramidValuesForGraphAndTable = (
  currentData: PyramidValueDto[],
  pastData: PyramidValueDto[],
  forecastData: PyramidForecastValues,
  recommendedData: ReferencePyramidDetail[],
  type: PyramidType,
): PyramidsData[] => {
  let pyramidsData: PyramidsData[] = []

  const sortedCurrentData = [...currentData].sort((x, y) => x.displayOrder - y.displayOrder)

  const forecastCombinedWithCurrent =
    type === PyramidType.Level
      ? combineForecastValuesWithCurrentValues(sortedCurrentData, forecastData)
      : combineForecastAgeValues(sortedCurrentData, forecastData.ageValues)

  const currentEmployeeCount = currentData.reduce((acc, level) => {
    acc += level.count
    return acc
  }, 0)
  const pastEmployeeCount = pastData.reduce((acc, level) => {
    acc += level.count
    return acc
  }, 0)
  const forecastEmployeeCount = forecastCombinedWithCurrent.reduce((acc, level) => {
    acc += level.count
    return acc
  }, 0)

  pyramidsData = sortedCurrentData.map(({ name, percentage }) => {
    const pastDataEntry = pastData.find((entry) => entry.name === name)
    const forecastDataEntry = forecastCombinedWithCurrent.find((entry) => entry.name === name)
    const recommendedDataEntry = recommendedData.find((entry) => entry.range === name)

    return {
      name,
      past: pastDataEntry ? Number(pastDataEntry.percentage.toFixed(2)) : undefined,
      current: Number(percentage.toFixed(2)),
      forecast: forecastDataEntry ? Number(forecastDataEntry.percentage.toFixed(2)) : undefined,
      limitType: recommendedDataEntry ? recommendedDataEntry.limitType : undefined,
      recommended: recommendedDataEntry ? Number(recommendedDataEntry.percentage.toFixed(2)) : undefined,
      recommendedRange: recommendedDataEntry
        ? calculateRecommendedValueRange(recommendedDataEntry.percentage, recommendedDataEntry.limitType)
        : undefined,
    }
  })

  // 'Total' row for the overview table
  pyramidsData.push({ name: 'total', past: pastEmployeeCount, current: currentEmployeeCount, forecast: forecastEmployeeCount })

  return pyramidsData
}

export const formatPyramidValuesForColumnGraph = (
  currentData: PyramidValueDto[],
  pastData: PyramidValueDto[],
  forecastData: PyramidForecastValues,
  recommendedData: ReferencePyramidDetail[],
  type: PyramidType,
): PyramidsColumnGraphData[] => {
  const sortedCurrentData = [...currentData].sort((x, y) => x.displayOrder - y.displayOrder)

  const forecastCombinedWithCurrent =
    type === PyramidType.Level
      ? combineForecastValuesWithCurrentValues(sortedCurrentData, forecastData)
      : combineForecastAgeValues(sortedCurrentData, forecastData.ageValues)

  return sortedCurrentData.map(({ name, percentage, count }) => {
    const pastDataEntry = pastData.find((entry) => entry.name === name)
    const forecastDataEntry = forecastCombinedWithCurrent.find((entry) => entry.name === name)
    const recommendedDataEntry = recommendedData.find((entry) => entry.range === name)

    return {
      name,
      past: pastDataEntry
        ? { percentage: Number(pastDataEntry.percentage.toFixed(2)), count: pastDataEntry.count }
        : undefined,
      current: { percentage: Number(percentage.toFixed(2)), count },
      forecast: forecastDataEntry
        ? { percentage: Number(forecastDataEntry.percentage.toFixed(2)), count: forecastDataEntry.count }
        : undefined,
      recommendedRange: recommendedDataEntry
        ? calculateRecommendedValueRange(recommendedDataEntry.percentage, recommendedDataEntry.limitType)
        : undefined,
    }
  })
}

export const formatPyramidValuesForDashboard = (
  currentData: PyramidValueDto[],
  recommendedData: ReferencePyramidDetail[],
): DashbaordPyramidsData[] => {
  let pyramidsData: DashbaordPyramidsData[] = []

  const sortedCurrentData = currentData.sort((x, y) => x.displayOrder - y.displayOrder)

  pyramidsData = sortedCurrentData.map(({ name, percentage, count }) => {
    const recommendedDataEntry = recommendedData.find((entry) => entry.range === name)

    return {
      name,
      current: Number(percentage.toFixed(2)),
      currentCount: count,
      recommended: recommendedDataEntry ? Number(recommendedDataEntry.percentage.toFixed(2)) : undefined,
      recommendedRange: recommendedDataEntry
        ? calculateRecommendedValueRange(recommendedDataEntry.percentage, recommendedDataEntry.limitType)
        : undefined,
    }
  })

  return pyramidsData
}

export const formatPyramidsValuesForCompanySettings = (
  recommendedData: ReferencePyramidDetail[],
  pyramidValues: PyramidValueDto[],
): PyramidsData[] => {
  let pyramidsData: PyramidsData[] = []

  const sortedRecommendedData = [...recommendedData].sort((x, y) => x.displayOrder - y.displayOrder)

  pyramidsData = sortedRecommendedData.map(({ range, limitType, percentage }) => {
    return {
      name: range,
      limitType,
      recommended: percentage,
      recommendedRange: calculateRecommendedValueRange(percentage, limitType),
    }
  })

  const currentEmployeeCount = pyramidValues.reduce((acc, level) => {
    acc += level.count
    return acc
  }, 0)
  pyramidsData.push({ name: 'total', past: 0, current: currentEmployeeCount, forecast: 0 })
  return pyramidsData
}

const calculateRecommendedValueRange = (recommended: number, limitType: string): [number, number] => {
  let recommendedRange: [number, number]

  if (limitType === LimitType.Min) {
    recommendedRange = [recommended, 100]
  } else {
    recommendedRange = [0, recommended]
  }

  return recommendedRange
}

// Forecast values have to be added on top of existing current values
export const combineForecastValuesWithCurrentValues = (
  currentData: PyramidValueDto[],
  { values, decrease }: PyramidForecastValues,
): PyramidValueDto[] => {
  if (values.length === 0 && decrease.length === 0) {
    return []
  }

  const currentTotalCount = currentData.reduce((acc, element) => {
    acc += element.count
    return acc
  }, 0)
  const forecastTotalCount = values.reduce((acc, element) => {
    acc += element.count
    return acc
  }, 0)
  const decreaseTotalCount = decrease.reduce((acc, element) => {
    acc += element.count
    return acc
  }, 0)

  const combinedTotalCount = currentTotalCount + forecastTotalCount - decreaseTotalCount

  const checkName = (currentName: string, forecastName: string): boolean => {
    const [l1, l2] = currentName.split('-')

    return forecastName === l1.trim() || forecastName === l2.trim()
  }

  const combinedData: PyramidValueDto[] = currentData.map((current) => {
    const relatedForecasts = values.filter((forecast) =>
      ['C1 - C2', 'PR - M', 'SPR - SM'].includes(current.name) ? checkName(current.name, forecast.name) : forecast.name === current.name,
    )
    const relatedDecrease = decrease.filter((decrease) =>
      ['C1 - C2', 'PR - M', 'SPR - SM'].includes(current.name) ? checkName(current.name, decrease.name) : decrease.name === current.name,
    )

    const relatedForecastCount = relatedForecasts.reduce((acc, element) => {
      acc += element.count
      return acc
    }, 0)
    const relatedDecreaseCount = relatedDecrease.reduce((acc, element) => {
      acc += element.count
      return acc
    }, 0)

    const count = current.count + relatedForecastCount - relatedDecreaseCount
    const percentage = count > 0 ? (count / combinedTotalCount) * 100 : 0

    return {
      name: current.name,
      count,
      percentage,
      displayOrder: current.displayOrder,
    }
  })

  return combinedData
}

export const mapCheckboxYearOptions = (option: Option): Option | undefined | null => {
  return (
    option.children && {
      ...option,
      children: option.children.map(({ label, ...rest }) => ({
        ...rest,
        label: (label = i18next.t(`global.month.${parseInt(rest.value)}`)),
      })),
    }
  )
}

export const combineForecastAgeValues = (
  pyramidValues: PyramidValueDto[],
  forecastValues: PyramidForecastValueDto[],
): PyramidValueDto[] => {
  if (!forecastValues || !forecastValues.length) {
    return []
  }

  const combined = pyramidValues.reduce((acc: PyramidValueDto[], current: PyramidValueDto) => {
    const forecastValue = forecastValues.find((forecast) => current.name === forecast.name)
    !!forecastValue ? acc.push({ ...current, count: forecastValue.count, percentage: forecastValue.percentage }) : acc.push(current)
    return acc
  }, [])

  return combined
}
